import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import UserProfile from '../..';
import UserPayroll from '../index';
import { useEosContext } from '../hooks/useEosContext';
// CO-670 — moved out of the EOS page into a shared model, so the Lifecycle Settings category picker
// and every reason label read from the same list.
import { departureReasonLabelKey } from '../../../../models/departureReasons';
import { DatePicker as CustomDatePicker, formatDateForInput } from '../../../../components/dateTimePickers';
import { SettlementLockedBanner } from './components/SettlementLockedBanner';
import SettlementScenarioComparison from './components/SettlementScenarioComparison';
import MarkSettlementPaidDialog from './components/MarkSettlementPaidDialog';
import ReopenSettlementDialog from './components/ReopenSettlementDialog';
import { buildSettlementLetterHtml } from './settlementLetter';
import {
  PreviewSettlement,
  ConfirmFinalSettlement,
  getSettlementAuditLog,
} from '../../../../api/Payroll/payroll';
import {
  PreviewSettlementInput,
  SettlementBreakdownView,
  SettlementAuditLogEntry,
  SettlementAuditAction,
  classifyInitiator,
  settlementNoticeRulesFor,
  settlementDeductionRefFor,
  settlementEmployerLoanCapFractionFor,
  gratuityLegalReferenceFor,
} from '../../../../models/payroll';
import StickyActionBar from '../../../../components/ui/StickyActionBar';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';
import { Checkbox } from '../../../../components/ui/Checkbox';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import { downloadHtmlAsPdf } from '../../../../utils/htmlToPdf';
import { formatNumber } from '../../../../utils';
import { parseApiUtc } from '@/utils/apiDate';

type Props = Readonly<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any> | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  role?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hireDate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CompanyCurrencyRate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedUser?: any;
  companyName?: string;
  companyLogo?: string;
  loggedInUserId?: string;
}>;

const FinalSettlement: React.FC<Props> = (props) => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const userId = id ?? '';
  // Final Settlement is HR-owned; Admin is a company admin, not HR (see CLAUDE.md Role Model). HR-only.
  const isHr = props.role === 'Hr';
  // Employee self-view: viewing your own settlement read-only → banner + no forms/actions/audit.
  const isOwnProfile = !!props.loggedInUserId && userId === props.loggedInUserId;
  const isSelfView = isOwnProfile && !isHr;

  const complianceCountry = useComplianceCountry();
  const { context, loading: contextLoading, refetch } = useEosContext(userId);

  const [noticePeriod, setNoticePeriod] = useState<string>('30');
  const [lastWorkingDay, setLastWorkingDay] = useState<string>('');
  const [compensationInLieu, setCompensationInLieu] = useState<boolean>(false);
  const [totalDeductions, setTotalDeductions] = useState<string>('0');
  const [employerLoanDeductions, setEmployerLoanDeductions] = useState<string>('');
  const [noticePeriodError, setNoticePeriodError] = useState<string>('');
  const [employerLoanError, setEmployerLoanError] = useState<string>('');

  const [preview, setPreview] = useState<SettlementBreakdownView | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [auditLog, setAuditLog] = useState<SettlementAuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [reopenOpen, setReopenOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { toast, showToast, dismissToast } = useToast();

  const currencySymbol =
    props.CompanyCurrencyRate?.currency?.currencySymbol ||
    props.CompanyCurrencyRate?.currencySymbol ||
    props.CompanyCurrencyRate?.currency?.currencyCode ||
    '';

  // Country + initiator + contract-type driven compliance rules (mirrors the backend
  // SettlementComplianceRules the PreviewSettlement endpoint enforces).
  const departureReason = context?.existingEos?.departureReason ?? '';
  const contractType = context?.existingEos?.contractType ?? '';
  const compliance = useMemo(() => {
    const initiator = classifyInitiator(departureReason);
    // Mirror backend exactly: fixed-term = contains "limited" AND NOT "unlimited"
    // (so an "Unlimited"/indefinite contract is NOT misclassified as fixed-term).
    const ct = (contractType ?? '').trim().toLowerCase();
    const isFixedTerm = ct.includes('limited') && !ct.includes('unlimited');
    return {
      notice: settlementNoticeRulesFor(complianceCountry, initiator, isFixedTerm),
      loanCapFraction: settlementEmployerLoanCapFractionFor(complianceCountry),
      deductionRef: settlementDeductionRefFor(complianceCountry),
    };
  }, [departureReason, contractType, complianceCountry]);

  // Settlement gross (before deductions) — only knowable once a preview exists; used for the
  // employer-loan 10% sub-cap client check.
  // Q11: the post-exit hourly line is earned wages and the server folds it into the same gross the
  // deduction cap is measured against, so this mirror has to include it or the client-side sub-cap check
  // would refuse a loan repayment the server would allow.
  const settlementGross = preview
    ? preview.gratuityAmount + preview.leaveEncashmentAmount + preview.proratedFinalMonthAmount
      + preview.compensationInLieuAmount + (preview.approvedHourlyAfterExitAmount ?? 0)
    : null;

  const loadAudit = useCallback(() => {
    if (!userId) return;
    setAuditLoading(true);
    getSettlementAuditLog(userId)
      .then(r => { if (!r.errors?.length) setAuditLog(r.payload || []); })
      .catch(() => {})
      .finally(() => setAuditLoading(false));
  }, [userId]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const validateNoticePeriod = (value: string): boolean => {
    const { notice } = compliance;
    // When no statutory notice applies (Neutral departure, or KSA fixed-term) the field is advisory only.
    if (!notice.noticeApplies) {
      setNoticePeriodError('');
      return true;
    }
    const n = Number(value);
    if (value.trim() === '' || Number.isNaN(n) || !Number.isInteger(n)) {
      setNoticePeriodError(t('Notice period must be a whole number'));
      return false;
    }
    if (n < notice.floorDays || n > notice.ceilingDays) {
      setNoticePeriodError(t('Notice period must be between {{floor}} and {{ceiling}} days', {
        floor: notice.floorDays,
        ceiling: notice.ceilingDays,
      }));
      return false;
    }
    setNoticePeriodError('');
    return true;
  };

  const validateEmployerLoan = (): boolean => {
    if (compliance.loanCapFraction == null) return true; // country models no employer-loan sub-cap
    const raw = employerLoanDeductions.trim();
    if (raw === '') { setEmployerLoanError(''); return true; } // optional
    const n = Number(raw);
    if (Number.isNaN(n) || n < 0) {
      setEmployerLoanError(t('Employer loan deductions must be zero or greater.'));
      return false;
    }
    const totalDed = Number(totalDeductions) || 0;
    if (n > totalDed) {
      setEmployerLoanError(t('Employer loan deductions cannot exceed the total deductions.'));
      return false;
    }
    // 10%-of-gross sub-cap — only checkable once a preview establishes the settlement gross.
    if (settlementGross != null && n > settlementGross * compliance.loanCapFraction) {
      const cap = settlementGross * compliance.loanCapFraction;
      setEmployerLoanError(t('Employer loan deductions of {{value}} exceed the {{percent}}% sub-cap of {{cap}} {{currency}} (KSA Labor Law Art. 92).', {
        value: formatNumber(n),
        percent: Math.round(compliance.loanCapFraction * 100),
        cap: formatNumber(cap),
        currency: currencySymbol,
      }));
      return false;
    }
    setEmployerLoanError('');
    return true;
  };

  const handleNoticePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoticePeriod(e.target.value);
    validateNoticePeriod(e.target.value);
  };

  const buildInput = (): PreviewSettlementInput => ({
    userId,
    noticePeriod: Number(noticePeriod) || 0,
    compensationInLieu,
    lastWorkingDay,
    totalDeductions: Number(totalDeductions) || 0,
    employerLoanDeductions: employerLoanDeductions.trim() === '' ? undefined : Number(employerLoanDeductions),
  });

  const handlePreview = async () => {
    if (!validateNoticePeriod(noticePeriod)) return;
    if (!validateEmployerLoan()) return;
    if (!lastWorkingDay) {
      showToast(t('Last working day is required'), 'info');
      return;
    }
    setPreviewing(true);
    try {
      const result = await PreviewSettlement(buildInput());
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0]?.description ?? t('Preview failed'), 'error');
        return;
      }
      setPreview(result.payload);
    } catch {
      showToast(t('An unexpected error occurred'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !lastWorkingDay) return;
    if (!validateEmployerLoan()) return;
    setConfirming(true);
    try {
      const result = await ConfirmFinalSettlement(buildInput());
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0]?.description ?? t('Failed to confirm settlement'), 'error');
        return;
      }
      showToast(t('Final settlement confirmed successfully'), 'success');
      setPreview(null);
      refetch();
      loadAudit();
    } catch {
      showToast(t('An unexpected error occurred'), 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
  };

  const handleReopened = () => {
    showToast(t('Settlement reopened successfully'), 'success');
    refetch();
    loadAudit();
  };

  const handleMarkedPaid = () => {
    showToast(t('Settlement marked as paid'), 'success');
    refetch();
    loadAudit();
  };

  const handleDownloadLetter = async () => {
    const breakdown = context?.existingSettlement;
    if (!breakdown) return;
    setDownloading(true);
    try {
      const html = buildSettlementLetterHtml(breakdown, {
        companyName: props.companyName || '',
        companyLogoUrl: props.companyLogo || undefined,
        employeeName: props.selectedUser?.fullName || '',
        employeeId: props.selectedUser?.employeeId || undefined,
        currencySymbol,
        country: complianceCountry,
        gratuityReference: gratuityLegalReferenceFor(complianceCountry),
        deductionReference: compliance.deductionRef,
      });
      const namePart = (props.selectedUser?.fullName || 'employee').replace(/\s+/g, '-').toLowerCase();
      await downloadHtmlAsPdf(html, `settlement-letter-${namePart}`);
    } catch {
      showToast(t('Failed to generate the settlement letter'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const auditActionLabel = (a: SettlementAuditAction): string => {
    switch (a) {
      case 'Confirmed': return t('Confirmed');
      case 'Reopened': return t('Reopened');
      case 'Paid': return t('Paid');
      case 'Reversed': return t('Reversed');
      default: return a;
    }
  };

  const auditActionTone = (a: SettlementAuditAction): string => {
    switch (a) {
      case 'Paid': return 'bg-primary/10 text-primary';
      case 'Reopened':
      case 'Reversed': return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const status = context?.settlementStatus;
  const isLocked = status === 'Confirmed' || status === 'Paid';
  const isLoading = contextLoading || previewing || confirming;

  // CO-279 — the settlement's leave encashment fell back to TOTAL GROSS because no basic wage could be
  // established. Same callout the End of Service screen renders for `context.noBasicConfigured`, so the two
  // screens read as one warning; the copy names LEAVE ENCASHMENT because that is the line this affects here.
  // Kept as a function so the preview and the locked (snapshot) breakdown share one implementation — a
  // pre-CO-279 snapshot simply has the field absent, so the warning stays hidden.
  const renderNoBasicWarning = (flagged?: boolean) =>
    flagged ? (
      <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
        <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
        <div className="flex flex-col gap-0.5">
          <p className="font-semibold">{t('No basic salary configured')}</p>
          <p className="text-warning/90">
            {t('Leave encashment is being calculated on gross pay because no salary component is flagged as the basic salary, which overstates the settlement. Flag the basic salary component in Salary Settings for an accurate amount.')}
          </p>
        </div>
      </div>
    ) : null;

  // CO-358 — the zero-base twin of renderNoBasicWarning. Same shape and same preview/snapshot reuse, but
  // destructive rather than warning: this is not an imprecise amount, it is an encashment of 0.00.
  // Never renders alongside the warning above — the server sets them on mutually exclusive branches.
  const renderZeroBaseWarning = (flagged?: boolean) =>
    flagged ? (
      <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
        <div className="flex flex-col gap-0.5">
          <p className="font-semibold">{t('Leave encashment base is zero')}</p>
          <p className="text-destructive/90">
            {t('The End of Service base is set to basic salary only, but no salary component is flagged as the basic salary, so the base is 0.00 and leave encashment computes to zero. Flag the basic salary component in Salary Settings, or change the End of Service base mode.')}
          </p>
        </div>
      </div>
    ) : null;

  if (complianceCountry !== 'UAE' && complianceCountry !== 'KSA') {
    return (
      <div className="flex flex-col flex-1">
        <UserProfile>
          <UserPayroll>
            <div className="relative flex flex-col gap-6 pt-5 pe-6">
              <div className="flex items-center gap-3">
                <AppIcon name="FileCheck" size={40} className="text-muted-foreground" />
                <h3 className="text-lg font-semibold text-muted-foreground">{t('FINAL SETTLEMENT')}</h3>
              </div>
              <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card py-12 px-6 text-center">
                <AppIcon name="Globe" size={48} className="text-muted-foreground" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{t('Final Settlement is configured for UAE only')}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('Final Settlement depends on UAE-specific End of Service rules. Support for your country will arrive in a future release.')}
                  </p>
                </div>
              </div>
            </div>
          </UserPayroll>
        </UserProfile>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
        <UserPayroll>
          <div className="relative flex flex-col gap-6 pt-5 pe-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              <AppIcon name="FileCheck" size={40} className="text-primary" />
              <h3 className="text-lg font-semibold text-primary">
                {t('FINAL SETTLEMENT')}
              </h3>
            </div>

            {/* Loading spinner while context loads */}
            {contextLoading && !context && (
              <div className="flex items-center justify-center py-16">
                <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            {context && (
              <>
                {/* Employee self-view — read-only notice */}
                {isSelfView && (
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
                    <AppIcon name="Eye" size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium text-foreground">{t('View only')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('This is your own final settlement. You can review the details, but only HR can make changes.')}
                      </p>
                    </div>
                  </div>
                )}

                {/* EOS prerequisite gate */}
                {!context.existingEos ? (
                  <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card py-12 px-6 text-center">
                    <AppIcon name="AlertCircle" size={48} className="text-muted-foreground" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{t('EOS Not Calculated Yet')}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('Please calculate End of Service entitlement first before creating a final settlement.')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Locked banner */}
                    {isLocked && (
                      <SettlementLockedBanner
                        status={status!}
                      />
                    )}

                    {/* Gratuity from EOS — always read-only */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-card border border-border rounded-xl p-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Gratuity (from EOS)')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatNumber(Number(context.existingEos.amount))} {currencySymbol}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Departure Reason')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {t(departureReasonLabelKey(context.existingEos.departureReason || ''))}
                        </p>
                      </div>
                    </div>

                    {/* CO-279 — gross-substitution warning for the stored (locked) breakdown */}
                    {isLocked && renderNoBasicWarning(context.existingSettlement?.noBasicConfigured)}
                    {/* CO-358 — zero-base callout for the stored (locked) breakdown */}
                    {isLocked && renderZeroBaseWarning(context.existingSettlement?.isSilentZeroBase)}

                    {/* Locked: render the stored breakdown read-only (B8a), else status-only fallback */}
                    {isLocked ? (
                      context.existingSettlement ? (
                        <div className="bg-card border border-border rounded-xl overflow-hidden">
                          <div className="flex items-center justify-between gap-3 px-5 py-3 bg-muted border-b border-border">
                            <h4 className="text-sm font-semibold text-foreground">{t('Settlement Details')}</h4>
                            <span className="text-xs font-medium text-muted-foreground">
                              {t('Status')}: <span className="text-foreground">{t(status!)}</span>
                            </span>
                          </div>
                          <div className="divide-y divide-border">
                            <div className="flex items-center justify-between px-5 py-3 text-sm">
                              <span className="text-muted-foreground">{t('Gratuity')}</span>
                              <span className="font-medium text-foreground">{formatNumber(context.existingSettlement.gratuityAmount)} {currencySymbol}</span>
                            </div>
                            <div className="flex items-center justify-between px-5 py-3 text-sm">
                              <span className="text-muted-foreground">{t('Leave Encashment')} ({context.existingSettlement.leaveDaysEncashed} {t('days')})</span>
                              <span className="font-medium text-foreground">{formatNumber(context.existingSettlement.leaveEncashmentAmount)} {currencySymbol}</span>
                            </div>
                            <div className="flex items-center justify-between px-5 py-3 text-sm">
                              <span className="text-muted-foreground">{t('Prorated Final Month')}</span>
                              <span className="font-medium text-foreground">{formatNumber(context.existingSettlement.proratedFinalMonthAmount)} {currencySymbol}</span>
                            </div>
                            {context.existingSettlement.compensationInLieuAmount > 0 && (
                              <div className="flex items-center justify-between px-5 py-3 text-sm">
                                <span className="text-muted-foreground">{t('Compensation in Lieu')}</span>
                                <span className="font-medium text-foreground">{formatNumber(context.existingSettlement.compensationInLieuAmount)} {currencySymbol}</span>
                              </div>
                            )}
                            {(context.existingSettlement.approvedHourlyAfterExitAmount ?? 0) > 0 && (
                              <div className="flex items-center justify-between px-5 py-3 text-sm" data-testid="settlement-hourly-after-exit">
                                <span className="text-muted-foreground">
                                  {t('Approved hourly pay after exit date')} ({context.existingSettlement.approvedHourlyAfterExitCount ?? 0} {t('shifts')})
                                </span>
                                <span className="font-medium text-foreground">{formatNumber(context.existingSettlement.approvedHourlyAfterExitAmount ?? 0)} {currencySymbol}</span>
                              </div>
                            )}
                            {context.existingSettlement.totalDeductions > 0 && (
                              <div className="flex items-center justify-between px-5 py-3 text-sm">
                                <span className="text-muted-foreground">{t('Deductions')}</span>
                                <span className="font-medium text-destructive">-{formatNumber(context.existingSettlement.totalDeductions)} {currencySymbol}</span>
                              </div>
                            )}
                            {context.existingSettlement.employerLoanDeductions != null && context.existingSettlement.employerLoanDeductions > 0 && (
                              <div className="flex items-center justify-between px-5 py-3 text-sm">
                                <span className="text-muted-foreground ps-4">{t('of which employer-loan repayment')}</span>
                                <span className="font-medium text-muted-foreground">-{formatNumber(context.existingSettlement.employerLoanDeductions)} {currencySymbol}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between px-5 py-4 text-sm bg-primary/5">
                              <span className="font-bold text-foreground text-base">{t('Total Settlement')}</span>
                              <span className="font-bold text-primary text-base">{formatNumber(context.existingSettlement.finalSettlementAmount)} {currencySymbol}</span>
                            </div>
                            {/* Payment record (Paid state) */}
                            {status === 'Paid' && (context.settlementPaidOn || context.settlementPaymentReference) && (
                              <div className="flex flex-col gap-1 px-5 py-3 text-sm bg-muted/40">
                                {context.settlementPaidOn && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">{t('Paid on')}</span>
                                    <span className="font-medium text-foreground">{appDate(context.settlementPaidOn)}</span>
                                  </div>
                                )}
                                {context.settlementPaymentReference && (
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-muted-foreground">{t('Payment reference')}</span>
                                    <span className="font-medium text-foreground break-all text-end">{context.settlementPaymentReference}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3">
                          <h4 className="text-sm font-semibold text-foreground">{t('Settlement Details')}</h4>
                          <p className="text-sm text-muted-foreground">
                            {t('Status')}: <span className="font-semibold text-foreground">{t(status!)}</span>
                          </p>
                        </div>
                      )
                    ) : (
                      /* Draft / no settlement — editable form */
                      isHr && (
                        <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-5">
                          <h4 className="text-sm font-semibold text-foreground">{t('Settlement Inputs')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Notice Period */}
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-1">
                                {t('Notice Period (days)')} {compliance.notice.noticeApplies && <span className="text-destructive">*</span>}
                              </label>
                              <input
                                type="number"
                                min={compliance.notice.noticeApplies ? compliance.notice.floorDays : 0}
                                max={compliance.notice.noticeApplies && compliance.notice.ceilingDays < 100000 ? compliance.notice.ceilingDays : undefined}
                                value={noticePeriod}
                                onChange={handleNoticePeriodChange}
                                className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${noticePeriodError ? 'border-destructive' : 'border-input'}`}
                              />
                              {noticePeriodError && (
                                <p className="mt-1 text-xs text-destructive">{noticePeriodError}</p>
                              )}
                              <p className="mt-1 text-xs text-muted-foreground">
                                {compliance.notice.noticeApplies
                                  ? t('Valid range: {{floor}}–{{ceiling}} days ({{reference}})', {
                                    floor: compliance.notice.floorDays,
                                    ceiling: compliance.notice.ceilingDays,
                                    reference: compliance.notice.legalReference,
                                    nsSeparator: false, // key contains a colon; keep the "Valid range:" prefix
                                  })
                                  : t('No statutory notice period applies for this departure reason.')}
                              </p>
                            </div>

                            {/* Last Working Day */}
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-1">
                                {t('Last Working Day')} <span className="text-destructive">*</span>
                              </label>
                              <CustomDatePicker
                                label=""
                                value={lastWorkingDay || null}
                                onChange={(d: Date | null) => setLastWorkingDay(d ? formatDateForInput(d) : '')}
                              />
                            </div>

                            {/* Deductions */}
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-1">
                                {t('Total Deductions')}
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={totalDeductions}
                                onChange={e => { setTotalDeductions(e.target.value); if (employerLoanError) setEmployerLoanError(''); }}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                              />
                            </div>

                            {/* Employer-loan deductions — only where the country models a sub-cap (KSA Art. 92) */}
                            {compliance.loanCapFraction != null && (
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                  {t('Employer Loan Deductions')}
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={employerLoanDeductions}
                                  onChange={e => { setEmployerLoanDeductions(e.target.value); if (employerLoanError) setEmployerLoanError(''); }}
                                  onBlur={validateEmployerLoan}
                                  className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${employerLoanError ? 'border-destructive' : 'border-input'}`}
                                />
                                {employerLoanError ? (
                                  <p className="mt-1 text-xs text-destructive">{employerLoanError}</p>
                                ) : (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {t('Loan repayment is capped at {{percent}}% of the settlement wage (KSA Labor Law Art. 92).', {
                                      percent: Math.round(compliance.loanCapFraction * 100),
                                    })}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Compensation in lieu toggle */}
                            <div className="flex items-center">
                              <Checkbox
                                checked={compensationInLieu}
                                onChange={() => setCompensationInLieu(v => !v)}
                                label={t('Compensation in Lieu of Notice')}
                              />
                            </div>
                          </div>

                          <div className="pt-2">
                            <Button
                              type="button"
                              onClick={handlePreview}
                              loading={previewing}
                              disabled={previewing}
                            >
                              {t('Preview Settlement')}
                            </Button>
                          </div>
                        </div>
                      )
                    )}

                    {/* CO-279 — gross-substitution warning for the live preview */}
                    {preview && renderNoBasicWarning(preview.noBasicConfigured)}
                    {/* CO-358 — zero-base callout for the live preview */}
                    {preview && renderZeroBaseWarning(preview.isSilentZeroBase)}

                    {/* Preview breakdown */}
                    {preview && (
                      <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <div className="px-5 py-3 bg-muted border-b border-border">
                          <h4 className="text-sm font-semibold text-foreground">{t('Settlement Breakdown')}</h4>
                        </div>
                        <div className="divide-y divide-border">
                          <div className="flex items-center justify-between px-5 py-3 text-sm">
                            <span className="text-muted-foreground">{t('Gratuity')}</span>
                            <span className="font-medium text-foreground">
                              {formatNumber(preview.gratuityAmount)} {currencySymbol}
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-5 py-3 text-sm">
                            <span className="text-muted-foreground">
                              {t('Leave Encashment')} ({preview.leaveDaysEncashed} {t('days')})
                            </span>
                            <span className="font-medium text-foreground">
                              {formatNumber(preview.leaveEncashmentAmount)} {currencySymbol}
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-5 py-3 text-sm">
                            <span className="text-muted-foreground">{t('Prorated Final Month')}</span>
                            <span className="font-medium text-foreground">
                              {formatNumber(preview.proratedFinalMonthAmount)} {currencySymbol}
                            </span>
                          </div>
                          {preview.compensationInLieuAmount > 0 && (
                            <div className="flex items-center justify-between px-5 py-3 text-sm">
                              <span className="text-muted-foreground">{t('Compensation in Lieu')}</span>
                              <span className="font-medium text-foreground">
                                {formatNumber(preview.compensationInLieuAmount)} {currencySymbol}
                              </span>
                            </div>
                          )}
                          {/* Q11 — approved hourly work dated after a back-dated exit. Ordinary earned
                              wages, so it reads exactly like the rows around it: no tint, no icon, no
                              emphasis. Styling it as an exception would imply HR has a decision to make
                              here, and they do not — the approval was already somebody's decision. */}
                          {(preview.approvedHourlyAfterExitAmount ?? 0) > 0 && (
                            <div className="flex items-center justify-between px-5 py-3 text-sm" data-testid="settlement-hourly-after-exit">
                              <span className="text-muted-foreground">
                                {t('Approved hourly pay after exit date')} ({preview.approvedHourlyAfterExitCount ?? 0} {t('shifts')})
                              </span>
                              <span className="font-medium text-foreground">
                                {formatNumber(preview.approvedHourlyAfterExitAmount ?? 0)} {currencySymbol}
                              </span>
                            </div>
                          )}
                          {preview.totalDeductions > 0 && (
                            <div className="flex items-center justify-between px-5 py-3 text-sm">
                              <span className="text-muted-foreground">{t('Deductions')}</span>
                              <span className="font-medium text-destructive">
                                -{formatNumber(preview.totalDeductions)} {currencySymbol}
                              </span>
                            </div>
                          )}
                          {preview.deductionCapApplied && (
                            <div className="flex items-start gap-2 px-5 py-3 bg-amber-50 dark:bg-amber-950 text-sm text-amber-700 dark:text-amber-300">
                              <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                              <p>
                                {t('Deduction cap applied — deductions limited to {{amount}} {{currency}} under {{reference}}.', {
                                  amount: formatNumber(preview.deductionCapWarning),
                                  currency: currencySymbol,
                                  reference: compliance.deductionRef,
                                })}
                              </p>
                            </div>
                          )}
                          <div className="flex items-center justify-between px-5 py-4 text-sm bg-primary/5">
                            <span className="font-bold text-foreground text-base">{t('Total Settlement')}</span>
                            <span className="font-bold text-primary text-base">
                              {formatNumber(preview.finalSettlementAmount)} {currencySymbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* What-if comparison (HR/Admin planning tool — no side effects) */}
                    {isHr && !isLocked && (
                      <SettlementScenarioComparison
                        base={{
                          userId,
                          noticePeriod: Number(noticePeriod) || 0,
                          compensationInLieu,
                          lastWorkingDay,
                          totalDeductions: Number(totalDeductions) || 0,
                          employerLoanDeductions: employerLoanDeductions.trim() === '' ? undefined : Number(employerLoanDeductions),
                        }}
                        currencySymbol={currencySymbol}
                      />
                    )}

                    {/* Confirm action bar */}
                    {isHr && !isLocked && (
                      <StickyActionBar
                        hasUnsavedChanges={!!preview}
                        isSaving={confirming}
                        onReset={handleReset}
                        onSave={handleConfirm}
                        t={t}
                        saveLabel={t('Confirm & Finalise')}
                        resetLabel={t('Clear Preview')}
                      />
                    )}

                    {/* Settlement lifecycle actions (locked state) */}
                    {isLocked && context.existingSettlement && (
                      <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground">{t('Settlement actions')}</h4>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {status === 'Paid'
                              ? t('This settlement has been paid. You can download the settlement letter.')
                              : t('Download the letter, record payment, or reopen to edit.')}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            iconName="Download"
                            loading={downloading}
                            disabled={downloading}
                            onClick={handleDownloadLetter}
                          >
                            {t('Download settlement letter')}
                          </Button>
                          {isHr && status === 'Confirmed' && (
                            <>
                              <Button
                                type="button"
                                variant="default"
                                iconName="BadgeCheck"
                                onClick={() => setMarkPaidOpen(true)}
                              >
                                {t('Mark as paid')}
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                iconName="Undo2"
                                onClick={() => setReopenOpen(true)}
                              >
                                {t('Reopen settlement')}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Settlement audit trail (B8b) */}
                    {isHr && (auditLoading || auditLog.length > 0) && (
                      <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3">
                        <h4 className="text-sm font-semibold text-foreground">{t('Settlement Audit Trail')}</h4>
                        {auditLoading ? (
                          <p className="text-sm text-muted-foreground">{t('Loading...')}</p>
                        ) : (
                          <ul className="flex flex-col">
                            {auditLog.map(entry => (
                              <li key={entry.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-t border-border text-sm first:border-t-0">
                                <span className="text-muted-foreground shrink-0 w-24">
                                  {appDate(parseApiUtc(entry.performedOn))}
                                </span>
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${auditActionTone(entry.action)}`}>
                                  {auditActionLabel(entry.action)}
                                </span>
                                <span className="text-muted-foreground truncate">
                                  {entry.paymentReference
                                    ? t('Ref: {{reference}}', { reference: entry.paymentReference, nsSeparator: false })
                                    : entry.reason || ''}
                                </span>
                                <span className="font-semibold text-foreground ms-auto" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatNumber(entry.amount)} {currencySymbol}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Overlay spinner for async operations */}
            {isLoading && context && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            <ToastRenderer toast={toast} onDismiss={dismissToast} />
          </div>

          {/* Lifecycle dialogs */}
          <ReopenSettlementDialog
            open={reopenOpen}
            onOpenChange={setReopenOpen}
            userId={userId}
            onSuccess={handleReopened}
          />
          <MarkSettlementPaidDialog
            open={markPaidOpen}
            onOpenChange={setMarkPaidOpen}
            userId={userId}
            amount={context?.existingSettlement?.finalSettlementAmount ?? 0}
            currencySymbol={currencySymbol}
            onSuccess={handleMarkedPaid}
          />
        </UserPayroll>
      </UserProfile>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
  role: state.user.role,
  hireDate: state.user.selectedUser?.hireDate ?? state.user.hireDate,
  CompanyCurrencyRate: state.user.CompanyCurrencyRate,
  selectedUser: state.user.selectedUser,
  companyName: state.user.companyName,
  companyLogo: state.user.companyLogo,
  loggedInUserId: state.user.userId,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps)(FinalSettlement) as any;
